'use client';
import { FileOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Upload, message } from 'antd';
import type { RcFile, UploadProps } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import getLocal from 'eco-design-reliance/getLocal';
import { useEffect, useRef, useState } from 'react';
import { supportedTypes } from './config';
import { formatUploaderText, getUploaderText } from './i18n';
import './index.scss';
import { UploaderProps } from './type.d';
import { uploadFile } from './upload';

const isSameUrls = (a: string[], b: string[]) => a.length === b.length && a.every((url, index) => url === b[index]);

/** 与 antd isImageUrl 对齐的常见图片后缀；无后缀时不要默认当图片（OSS 直链常无扩展名） */
const IMAGE_EXT_RE = /\.(webp|svg|png|gif|jpg|jpeg|jfif|bmp|dpg|ico|heic|heif)$/i;

const getFileNameFromUrl = (url: string, index: number) => {
    const raw = url.split('/').pop()?.split(/[#?]/)[0] || `file-${index + 1}`;
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
};

const isImageFileName = (name?: string) => !!name && IMAGE_EXT_RE.test(name);

export default function Uploader(props: UploaderProps) {
    const { types = supportedTypes, size = 10 * 1024 * 1024, onChange, value = [], maxCount = 1, ...restProps } = props;
    const { locale = 'us' } = getLocal();
    const text = getUploaderText(locale);

    const [loading, setLoading] = useState(false);
    /** 已上传 url 的权威副本。多文件并发上传时各自读写同一个 ref，避免 setState 闭包互相覆盖 */
    const urlsRef = useRef<string[]>(value);
    /** 进行中的上传数量，全部结束才关掉 loading */
    const pendingRef = useRef(0);
    /**
     * 远端 url -> 本地 object URL。
     * us/it 站点接入了 consentmanager 自动拦截，会把未在白名单里的外域 <img src> 摘成 data-cmp-src，
     * 导致缩略图空白。预览统一走同源的 blob 地址，不依赖 CMP 放行。
     * 仅图片会写入；非图片不写，避免 thumbUrl 被当成 <img src>。
     */
    const previewMapRef = useRef<Map<string, string>>(new Map());

    const isImageUploadFile = (file: UploadFile) => {
        if (file.type?.startsWith('image/')) return true;
        if (file.type && !file.type.startsWith('image/')) return false;
        if (file.url && previewMapRef.current.has(file.url)) return true;
        return isImageFileName(file.name) || isImageFileName(file.url);
    };

    const toUploadFileList = (urls: string[]): UploadFile[] =>
        urls.map((url, index) => {
            const name = getFileNameFromUrl(url, index);
            const preview = previewMapRef.current.get(url);
            const isImage = !!preview || isImageFileName(name);
            return {
                uid: `${index}::${url}`,
                name,
                status: 'done' as const,
                url,
                // 只有图片才设 thumbUrl；非图片交给 iconRender 用通用文件图标
                ...(isImage ? { thumbUrl: preview || url } : {}),
            };
        });

    const [fileList, setFileList] = useState<UploadFile[]>(() => toUploadFileList(value));

    // 仅在外部 value 与内部记录内容不一致时才回灌，否则会把刚上传的结果洗掉
    useEffect(() => {
        if (isSameUrls(value, urlsRef.current)) return;
        urlsRef.current = value;
        setFileList(toUploadFileList(value));
    }, [value]);

    useEffect(() => {
        const previews = previewMapRef.current;
        return () => {
            previews.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
            previews.clear();
        };
    }, []);

    const commit = (urls: string[]) => {
        urlsRef.current = urls;
        setFileList(toUploadFileList(urls));
        onChange(urls);
    };

    const getTypeTips = (types: any) => {
        const type1 = types.map((item: any) => {
            if (typeof item === 'string') {
                if (item.includes('/')) {
                    return item.split('/')[1] || '';
                } else {
                    return item;
                }
            }
            return item;
        });
        return type1.join();
    };

    const beforeUpload = (file: RcFile, batch: RcFile[]) => {
        // 有些文件找不到type，通过后缀来限制
        const fileExtension = file.name.slice(file.name.lastIndexOf('.') + 1);

        if (types?.length > 0 && !types.includes(file?.type) && !types.includes(fileExtension)) {
            message.error(formatUploaderText(text.formatError, { types: getTypeTips(types) }));
            return false;
        }
        if (size < file.size) {
            message.error(formatUploaderText(text.sizeError, { size: Math.round((size / 1024 / 1024) * 10) / 10 }));
            return false;
        }
        // 受控模式下 antd 的 maxCount 不会裁剪我们自己的 fileList，这里自行拦截超出的部分
        const allowed = Math.max(0, maxCount - urlsRef.current.length);
        const indexInBatch = batch.indexOf(file);
        if (indexInBatch >= allowed) {
            if (indexInBatch === allowed) {
                const maxCountKey = maxCount > 1 ? 'maxCountErrorPlural' : 'maxCountError';
                message.error(formatUploaderText(text[maxCountKey], { maxCount }));
            }
            return false;
        }
        return true;
    };

    const customRequest: UploadProps['customRequest'] = async ({ file, onSuccess, onError }) => {
        pendingRef.current += 1;
        setLoading(true);

        try {
            const uploadFileObj = file as RcFile;
            const url = await uploadFile(uploadFileObj, locale);

            if (uploadFileObj.type?.startsWith('image/')) {
                previewMapRef.current.set(url, URL.createObjectURL(uploadFileObj));
            }
            commit([...urlsRef.current, url].slice(0, maxCount));
            onSuccess?.(url);
        } catch (error) {
            onError?.(error as Error);
            console.dir(error);
            // console.error(error);
            message.error(text.uploadFailed);
        } finally {
            pendingRef.current = Math.max(0, pendingRef.current - 1);
            if (pendingRef.current === 0) {
                setLoading(false);
            }
        }
    };

    const onRemove = (file: UploadFile) => {
        const preview = file.url ? previewMapRef.current.get(file.url) : undefined;
        if (preview && file.url) {
            URL.revokeObjectURL(preview);
            previewMapRef.current.delete(file.url);
        }
        commit(urlsRef.current.filter((url) => url !== file.url));
    };

    const uploadButton = <div>{loading ? <LoadingOutlined /> : <PlusOutlined />}</div>;

    const uploadProps: UploadProps = {
        ...restProps,
        className: ['comps-uploader', restProps.className].filter(Boolean).join(' '),
        fileList,
        maxCount,
        listType: 'picture-card',
        multiple: maxCount > 1,
        showUploadList: {
            showPreviewIcon: false,
            showRemoveIcon: true,
            showDownloadIcon: false,
        },
        // 覆盖 antd 默认「无后缀当图片」的判断，避免 pdf 等被塞进 <img>
        isImageUrl: isImageUploadFile,
        // picture-card 在 isImageUrl=false 时走这里；用 antd 通用文件图标即可
        iconRender: () => <FileOutlined style={{ color: '#1677FF' }} />,
        customRequest,
        beforeUpload,
        onRemove,
    };

    return <Upload {...uploadProps}>{fileList.length < maxCount ? uploadButton : null}</Upload>;
}
